import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import '@/App.css';

// Context Providers
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';

// Layout Components
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';
import { CartDrawer } from './components/layout/CartDrawer';
import { ScrollToTop } from './components/layout/ScrollToTop';

// Route Protection
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';

// Admin Pages
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminOrdersPage from './pages/admin/AdminOrdersPage';
import AdminProductsPage from './pages/admin/AdminProductsPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminReservationPage from './pages/admin/AdminReservationPage';
import AdminAddProductPage from "./pages/admin/AdminAddProductPage";

// Pages
import HomePage from './pages/HomePage';
import MenuPage from './pages/MenuPage';
import ProductPage from './pages/ProductPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AccountPage from './pages/AccountPage';
import AboutPage from './pages/AboutPage';
import GalleryPage from './pages/GalleryPage';
import ContactPage from './pages/ContactPage';
import ReservationPage from './pages/ReservationPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import NewsletterPage from './pages/NewsletterPage';
import OrderDetailsPage from './pages/OrderDetailsPage';
import ConfidentialitePage from './pages/ConfidentialitePage';
import MentionsLegalesPage from './pages/MentionsLegalesPage';
import VerificationEmailPage from './pages/VerificationEmailPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import AdminNewsletterPage from './pages/admin/AdminNewsletterPage';
import SiteBanner from "./components/ui/SiteBanner";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import MaintenancePage, {
  isMaintenanceActive,
  isPathAllowedDuringMaintenance,
} from "./pages/MaintenancePage";
import { useAuth } from "./context/AuthContext";

// Pages POS
import POSDashboard from "./pages/pos/POSDashboard";
import POSCheckout from "./pages/pos/POSCheckout";
import POSSalesHistory from "./pages/pos/POSSalesHistory";
import POSClosingPage from "./pages/pos/POSClosingPage";

// Cookie Banner Components
import CookieBanner from './components/CookieBanner';

function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    if (!window.gtag) return;

    window.gtag('event', 'page_view', {
      page_title: document.title,
      page_path: location.pathname + location.search,
      page_location: window.location.href,
    });
  }, [location]);

  return null;
}


function PageTitleManager() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;

    const titles = {
      "/": "Accueil",
      "/menu": "Notre carte",
      "/panier": "Mon panier",
      "/checkout": "Finaliser ma commande",
      "/connexion": "Connexion",
      "/inscription": "Créer un compte",
      "/mot-de-passe-oublie": "Mot de passe oublié",
      "/reset-password": "Réinitialiser le mot de passe",
      "/mon-compte": "Mon compte",
      "/a-propos": "À propos",
      "/galerie": "Galerie",
      "/contact": "Contact",
      "/reservation": "Réservation",
      "/newsletter": "Newsletter",
      "/mentions-legales": "Mentions légales",
      "/confidentialite": "Politique de confidentialité",
      "/verification-email": "Vérification de l'e-mail",
      "/paiement-reussi": "Paiement réussi",
      "/paiement-annule": "Paiement annulé",

      // Administration
      "/admin": "Administration",
      "/admin/dashboard": "Tableau de bord",
      "/admin/orders": "Gestion des commandes",
      "/admin/products": "Gestion des produits",
      "/admin/products/new": "Ajouter un produit",
      "/admin/users": "Gestion des clients",
      "/admin/settings": "Paramètres",
      "/admin/reservations": "Gestion des réservations",
      "/admin/newsletter": "Gestion de la newsletter",

      // POS
      "/pos": "Caisse",
      "/pos/checkout": "Encaissement",
      "/pos/sales": "Historique des ventes",
      "/pos/closing": "Clôture de caisse",
    };

    let pageTitle = titles[path];

    if (!pageTitle && path.startsWith("/produit/")) {
      pageTitle = "Détails du produit";
    }

    if (!pageTitle && path.startsWith("/commande/")) {
      pageTitle = "Détails de la commande";
    }

    document.title = pageTitle
      ? `${pageTitle} | Léger's Coffee`
      : "Léger's Coffee";

  }, [location.pathname]);

  return null;
}


// Layout wrapper component

const Layout = ({ children }) => {
  const location = useLocation();
  const { isAdmin } = useAuth();

  // Mode maintenance : les visiteurs voient la page de maintenance,
  // les administrateurs voient le site normalement.
  if (
    isMaintenanceActive() &&
    !isAdmin &&
    !isPathAllowedDuringMaintenance(location.pathname)
  ) {
    return <MaintenancePage />;
  }

  const isPOS =
    location.pathname === "/pos" ||
    location.pathname.startsWith("/pos/");

  if (isPOS) {
    return (
      <div className="min-h-screen bg-[#F7F3ED]">
        {children}
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-warm-alabaster">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
      <CookieBanner />
      <CartDrawer />
      <SiteBanner />
    </div>
  );
};


function App() {
  return (
    <BrowserRouter>
      <AnalyticsTracker />

      <AuthProvider>
        <CartProvider>
          <ScrollToTop />
          <Layout>
            <Routes>
              {/* Public Pages */}
              <Route path="/" element={<HomePage />} />
              <Route path="/menu" element={<MenuPage />} />
              <Route path="/produit/:slug" element={<ProductPage />} />
              <Route path="/panier" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/a-propos" element={<AboutPage />} />
              <Route path="/galerie" element={<GalleryPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/reservation" element={<ReservationPage />} />
              <Route path="/newsletter" element={<NewsletterPage />} />
              <Route path="/commande/:id" element={<OrderDetailsPage />} />
              <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
              <Route path="/confidentialite" element={<ConfidentialitePage />} />
              <Route path="/verification-email" element={<VerificationEmailPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              <Route
                path="/reset-password"
                element={<ResetPasswordPage />}
              />

              {/* Auth Pages */}
              <Route path="/connexion" element={<LoginPage />} />
              <Route path="/inscription" element={<RegisterPage />} />
              <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />

              {/* Account */}
              <Route
                path="/mon-compte"
                element={
                  <ProtectedRoute>
                    <AccountPage />
                  </ProtectedRoute>
                }
              />

              {/* Admin */}
              <Route element={<AdminRoute />}>
                <Route path="/admin" element={<AdminDashboardPage />} />
                <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
                <Route path="/admin/orders" element={<AdminOrdersPage />} />
                <Route path="/admin/products" element={<AdminProductsPage />} />
                <Route path="/admin/users" element={<AdminUsersPage />} />
                <Route path="/admin/settings" element={<AdminSettingsPage />} />
                <Route path="/admin/reservations" element={<AdminReservationPage />} />
                <Route path="/admin/newsletter" element={<AdminNewsletterPage />} />
                <Route path="/admin/products/new" element={<AdminAddProductPage />} />
                {/* POS — accès administrateur provisoire */}
                <Route path="/pos" element={<POSDashboard />} />
                <Route path="/pos/checkout" element={<POSCheckout />} />
                <Route path="/pos/sales" element={<POSSalesHistory />} />
                <Route path="/pos/closing" element={<POSClosingPage />} />
              </Route>

              {/* Payment */}
              <Route path="/paiement-reussi" element={<PaymentSuccessPage />} />
              <Route path="/paiement-annule" element={<PaymentCancelPage />} />

              {/* 404 */}
              <Route path="*" element={<HomePage />} />
            </Routes>
          </Layout>

          <Toaster
            position="bottom-right"
            toastOptions={{
              style: {
                background: '#FFFCF8',
                border: '1px solid #E6E1D6',
                color: '#2A2A2A',
              },
              className: 'font-sans',
            }}
          />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter >
  );
}

export default App;